import { Builder } from '@builder.io/react';
import { FC, useState } from 'react';
import cn from 'classnames';

import { Tab, Tabs } from '@catawiki/cata-style';

import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  backgroundColorInput,
  componentLinkIdInput,
  iconInput,
} from '@/shared/inputs';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { getBuilderString } from '@/shared/text';
import { IconMap } from '@/shared/constants/icons';

import { flatMediaInput } from '../Media';
import { TabContent, TabInput } from './TabContent';
import sharedStyle from '../shared.module.scss';
import styles from './VideoTabs.module.scss';
import { ActionVariant, getActionVariantName } from '../Action';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  tabs: TabInput[];
}

interface Props extends BuilderInput {
  locale: string;
}

export const VideoTabs: FC<Props> = ({
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
  componentLinkId,
  locale,
  tabs,
}) => {
  const [tabIndex, setTabIndex] = useState<number>(0);

  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  return (
    <section
      data-testid="videoTabs"
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className={cn('u-wrapper')}>
        <Tabs variant="scrollable">
          {tabs.map((tab, index) => {
            const Icon = IconMap[tab.tabIcon ?? 'no-icon'];
            return (
              <Tab
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                label={tab.tabName}
                active={tabIndex === index}
                onClick={() => setTabIndex(index)}
              >
                <div
                  className={cn(
                    'u-flex',
                    'u-flex--direction-column',
                    'u-flex--align-items-center'
                  )}
                >
                  {Icon && <Icon className={styles.icon} />}
                  <span className={cn('u-m-t-xxs', 'u-typography-body-s')}>
                    {getBuilderString(tab.tabName, blockLocale)}
                  </span>
                </div>
              </Tab>
            );
          })}
        </Tabs>
        {tabs.map((tab, index) => (
          <TabContent
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={cn('u-m-t-xxl-6', { 'u-hide': index !== tabIndex })}
            {...tab}
            locale={blockLocale}
          />
        ))}
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'VideoTabs';

export function componentRegister() {
  Builder.registerComponent(VideoTabs, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'tabs',
        friendlyName: 'Tabs',
        type: 'list',
        subFields: [
          {
            name: 'tabName',
            friendlyName: 'Tab Name',
            type: 'CatawikiPlainEditor',
            required: true,
          },
          iconInput('tabIcon', 'Tab Icon'),
          {
            name: 'content',
            type: 'CatawikiEditor',
          },
          ...flatMediaInput({ useCatawikiPlainEditor: true }),
          {
            name: 'actionVariant',
            type: 'string',
            enum: [
              ActionVariant.NONE,
              ActionVariant.PRIMARY,
              ActionVariant.SECONDARY,
              ActionVariant.TERTIARY,
            ].map((name) => ({
              label: getActionVariantName(name),
              value: name,
            })),
            defaultValue: ActionVariant.NONE.toString(),
          },
          {
            name: 'actionLabel',
            friendlyName: 'CTA label',
            type: 'CatawikiPlainEditor',
          },
          {
            name: 'actionUrl',
            friendlyName: 'CTA url',
            type: 'CatawikiPlainEditor',
          },
        ],
        defaultValue: [
          {
            tabName: 'Video Tab',
            tabIcon: 'no-icon',
            title: '',
            content: '',
            mediaType: 'youtube',
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F133333c1d9bc4d2387914212d38d9c4a%2F36fc257efd89439da97f659b9d5a4f3a',
            youtubeUrl: 'https://youtu.be/DWKlpw8_Ayc',
          },
        ],
      },
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
